package org.teddapp.project.ui.focus

import com.tedd.db.Timer_state
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.launch
import org.teddapp.project.db.nowMillis

/** Une course du minuteur, publiée quand elle se termine ou qu'on l'arrête après au moins une minute. */
data class TimerRun(val startedAt: Long, val endedAt: Long, val minutes: Int, val plannedMinutes: Int, val itemId: String?, val completed: Boolean)

/**
 * Chronomètre partagé. Rien n'est décompté localement : l'état vient d'une ligne synchronisée
 * (instant de départ, durée prévue, temps cumulé en pause) et le temps restant se recalcule
 * depuis l'horloge à chaque seconde. Une course lancée sur le téléphone s'affiche donc à
 * l'identique sur l'ordinateur, et survit à la fermeture de l'application.
 */
object FocusTimerController {
    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
    private var ticker: Job? = null

    /** Courses terminées ; l'écran Focus les enregistre et propose la phrase de fin. */
    val runs = MutableSharedFlow<TimerRun>(extraBufferCapacity = 8)

    /** Ligne partagée la plus récente, poussée par l'écran qui observe la base. */
    private val state = MutableStateFlow<Timer_state?>(null)

    val active = MutableStateFlow(false)
    val paused = MutableStateFlow(false)
    val totalSec = MutableStateFlow(0)
    val remainingSec = MutableStateFlow(0)
    val itemId = MutableStateFlow<String?>(null)

    /** Vrai quand la course a été lancée depuis un autre appareil. */
    val remoteDevice = MutableStateFlow<String?>(null)

    /** Signalée une fois par course pour éviter d'enregistrer deux fois la même fin. */
    private var completedStartedAt: Long? = null

    /** Reçoit la ligne synchronisée et relance le calcul. */
    fun adopt(row: Timer_state?, thisDevice: String) {
        state.value = row
        remoteDevice.value = row?.device?.takeIf { it != thisDevice && row.running == 1L }
        recompute()
        ensureTicker()
    }

    private fun ensureTicker() {
        if (ticker?.isActive == true) return
        ticker = scope.launch {
            while (true) {
                delay(1000L)
                recompute()
            }
        }
    }

    /** Temps écoulé réel, pauses déduites. */
    private fun elapsedMs(row: Timer_state, now: Long): Long {
        val started = row.started_at ?: return 0L
        val pausedNow = row.paused_at?.let { (now - it).coerceAtLeast(0L) } ?: 0L
        return (now - started - row.paused_accum_ms - pausedNow).coerceAtLeast(0L)
    }

    private fun recompute() {
        val row = state.value
        if (row == null || row.running == 0L || row.started_at == null) {
            active.value = false
            paused.value = false
            itemId.value = null
            totalSec.value = (row?.planned_minutes?.toInt() ?: 25) * 60
            remainingSec.value = totalSec.value
            return
        }
        val now = nowMillis()
        val total = row.planned_minutes.toInt() * 60
        val elapsedSec = (elapsedMs(row, now) / 1000L).toInt()
        val remaining = (total - elapsedSec).coerceAtLeast(0)
        totalSec.value = total
        remainingSec.value = remaining
        paused.value = row.paused_at != null
        active.value = remaining > 0
        itemId.value = row.item_id

        // Fin de course : publiée une seule fois, par l'appareil qui la voit arriver.
        if (remaining == 0 && completedStartedAt != row.started_at) {
            completedStartedAt = row.started_at
            runs.tryEmit(
                TimerRun(
                    startedAt = row.started_at!!,
                    endedAt = now,
                    minutes = row.planned_minutes.toInt(),
                    plannedMinutes = row.planned_minutes.toInt(),
                    itemId = row.item_id,
                    completed = true,
                ),
            )
        }
    }

    /**
     * Course arrêtée à la main : renvoie la course à enregistrer si elle a duré au moins
     * une minute, sinon null. L'écriture de la ligne partagée est faite par l'appelant.
     */
    fun runOnManualStop(): TimerRun? {
        val row = state.value ?: return null
        if (row.running == 0L || row.started_at == null) return null
        val now = nowMillis()
        val elapsedSec = (elapsedMs(row, now) / 1000L).toInt()
        if (elapsedSec < 60) return null
        completedStartedAt = row.started_at
        return TimerRun(
            startedAt = row.started_at!!,
            endedAt = now,
            minutes = elapsedSec / 60,
            plannedMinutes = row.planned_minutes.toInt(),
            itemId = row.item_id,
            completed = false,
        )
    }
}
